/**
 * Panel lateral de la consola de Rastreo: buscador, filtro, botón «+» y la lista
 * de equipos agrupada por cliente (RR-01, RR-02, RR-03).
 *
 * La forma es la de Traccar —buscador arriba, filas con ícono, nombre y estado,
 * íconos de ignición y batería a la derecha— y la agrupación es la de WOX: un
 * grupo por cliente, plegable, con su número de equipos.
 *
 * Cada estado lleva su palabra. Los íconos de ignición y batería llevan su texto
 * para lectores de pantalla y en el `title`; nada depende solo del color.
 *
 * Arriba de los grupos van los GPS que ya transmiten y nadie registró (RR-23): con
 * los últimos 4 dígitos del IMEI y un botón para registrarlos sin escribirlo.
 */

import { useId, useState } from "react";
import type { RastreoGroup, RastreoRow, RowFilter } from "@core/rastreo/registro.ts";
import { PROTOCOL_LABEL, type RastreoPendiente } from "@core/contracts/rastreo-registro.ts";
import { ROW_FILTER_LABEL } from "@core/rastreo/registro.ts";
import {
  BatteryIcon,
  CarIcon,
  ChevronIcon,
  FilterIcon,
  IgnitionIcon,
  PlusIcon,
  SearchIcon,
} from "./rastreo-icons.tsx";
import {
  ago,
  batteryOf,
  batteryText,
  ignitionOf,
  ignitionText,
  ROW_STATE_CLASS,
  ROW_STATE_LABEL,
} from "./rastreo-format.ts";

const FILTERS: RowFilter[] = ["todos", "en_linea", "sin_conexion", "pendientes"];

export function DeviceList({
  groups,
  total,
  shown,
  query,
  onQuery,
  filter,
  onFilter,
  selectedKey,
  onSelect,
  onAdd,
  pendientes = [],
  onRegisterPendiente = () => {},
  now = 0,
  notices,
  footer,
}: {
  groups: RastreoGroup[];
  /** Equipos en total y los que quedan después de buscar y filtrar. */
  total: number;
  shown: number;
  query: string;
  onQuery: (value: string) => void;
  filter: RowFilter;
  onFilter: (value: RowFilter) => void;
  selectedKey: string | null;
  onSelect: (row: RastreoRow) => void;
  onAdd: () => void;
  /** GPS que ya transmiten sin registro (RR-23). */
  pendientes?: RastreoPendiente[];
  onRegisterPendiente?: (item: RastreoPendiente) => void;
  /** Para «última trama hace …». */
  now?: number;
  /** Avisos sobre el origen de los datos: motor caído, registro sin leer… */
  notices: React.ReactNode;
  footer: React.ReactNode;
}) {
  const searchId = useId();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  const toggleGroup = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-gd-line bg-white p-3">
        <div className="flex items-center gap-2">
          <label htmlFor={searchId} className="sr-only">
            Buscar dispositivos
          </label>
          <div className="relative min-w-0 flex-1">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3.5 size-5 -translate-y-1/2 text-gd-muted" />
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={(event) => onQuery(event.target.value)}
              placeholder="Buscar dispositivos"
              autoComplete="off"
              className="min-h-11 pl-11"
            />
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls={`${searchId}-filtros`}
            className={`flex size-11 shrink-0 items-center justify-center rounded-xl border transition-colors ${
              filter !== "todos" || filtersOpen
                ? "border-gd-cyan bg-gd-mist text-gd-blue"
                : "border-gd-line-strong text-gd-navy hover:bg-gd-mist"
            }`}
          >
            <FilterIcon />
            <span className="sr-only">Filtrar por estado{filter !== "todos" ? `: ${ROW_FILTER_LABEL[filter]}` : ""}</span>
          </button>
          <button
            type="button"
            onClick={onAdd}
            className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-gd-cyan to-gd-blue text-white shadow-[var(--shadow-primary)] transition hover:brightness-105"
          >
            <PlusIcon />
            <span className="sr-only">Agregar dispositivo</span>
          </button>
        </div>

        {filtersOpen ? (
          <fieldset id={`${searchId}-filtros`} className="mt-3">
            <legend className="sr-only">Mostrar</legend>
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((option) => (
                <label
                  key={option}
                  className={`cursor-pointer rounded-full px-3 py-1.5 text-[13px] font-semibold ring-1 ring-inset transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-gd-cyan ${
                    filter === option
                      ? "bg-gd-navy text-white ring-gd-navy"
                      : "bg-white text-gd-navy ring-gd-line-strong hover:bg-gd-mist"
                  }`}
                >
                  <input
                    type="radio"
                    name={`${searchId}-filtro`}
                    value={option}
                    checked={filter === option}
                    onChange={() => onFilter(option)}
                    className="sr-only"
                  />
                  {ROW_FILTER_LABEL[option]}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
      </div>

      {notices}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" aria-label="Dispositivos" role="region">
        {pendientes.length > 0 ? (
          <PendingSection items={pendientes} now={now} onRegister={onRegisterPendiente} />
        ) : null}
        {total === 0 && pendientes.length > 0 ? null : total === 0 ? (
          <EmptyList
            title="Todavía no hay equipos"
            body="Agrega el primero con el botón «+». Registra lo que el GPS no transmite: modelo, SIM, ciudad, placa y cliente."
          />
        ) : shown === 0 ? (
          <EmptyList title="Ningún equipo coincide" body="Prueba con otra búsqueda o quita el filtro." />
        ) : (
          groups.map((group) => {
            const open = !collapsed.has(group.key);
            const listId = `${searchId}-${group.key}`;
            return (
              <section key={group.key} aria-label={`${group.title}: ${group.rows.length} equipo(s)`}>
                <h3 className="sticky top-0 z-[1] m-0">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={open}
                    aria-controls={listId}
                    className="flex w-full items-center gap-2 border-y border-gd-line bg-gd-mist px-4 py-2 text-left font-display text-[13px] font-bold tracking-wide text-gd-navy uppercase"
                  >
                    <ChevronIcon open={open} className="size-4 text-gd-blue" />
                    <span className="min-w-0 flex-1 truncate">{group.title}</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[12px] font-semibold text-gd-muted ring-1 ring-gd-line">
                      {group.rows.length}
                    </span>
                  </button>
                </h3>
                {open ? (
                  <ul id={listId} className="m-0 list-none p-0">
                    {group.rows.map((row) => (
                      <DeviceRow
                        key={row.key}
                        row={row}
                        selected={row.key === selectedKey}
                        onSelect={() => onSelect(row)}
                      />
                    ))}
                  </ul>
                ) : null}
              </section>
            );
          })
        )}
      </div>

      {footer}
    </div>
  );
}

function PendingSection({
  items,
  now,
  onRegister,
}: {
  items: RastreoPendiente[];
  now: number;
  onRegister: (item: RastreoPendiente) => void;
}) {
  return (
    <section aria-label={`Transmitiendo sin registrar: ${items.length} equipo(s)`}>
      <h3 className="m-0 flex items-center gap-2 border-y border-amber-200 bg-amber-50 px-4 py-2 font-display text-[13px] font-bold tracking-wide text-amber-950 uppercase">
        <span className="min-w-0 flex-1 truncate">Transmitiendo sin registrar</span>
        <span className="rounded-full bg-white px-2 py-0.5 text-[12px] font-semibold text-amber-900 ring-1 ring-amber-200">
          {items.length}
        </span>
      </h3>
      <p className="m-0 bg-white px-4 pt-2 text-[12px] text-gd-muted">
        Ya apuntan al servidor. Antes de registrar uno, compara los últimos 4 dígitos con la etiqueta del equipo.
      </p>
      <ul className="m-0 list-none p-0">
        {items.map((item) => (
          <li key={item.ref} className="border-b border-gd-line last:border-b-0">
            <button
              type="button"
              onClick={() => onRegister(item)}
              className="flex w-full items-center gap-3 bg-white px-4 py-3 text-left transition-colors hover:bg-gd-mist/70"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-[15px] font-semibold text-gd-navy">
                  IMEI terminado en {item.identificadorFinal}
                </span>
                <span className="block truncate text-[13px] text-gd-muted">
                  {item.protocolo ? PROTOCOL_LABEL[item.protocolo].split(" (")[0] : "Protocolo desconocido"} · última trama{" "}
                  {ago(item.ultimaTrama, now)}
                </span>
              </span>
              <span className="shrink-0 rounded-full bg-gd-navy px-3 py-1 text-[13px] font-semibold text-white">Registrar</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DeviceRow({ row, selected, onSelect }: { row: RastreoRow; selected: boolean; onSelect: () => void }) {
  const battery = batteryOf(row.vehicle);
  const ignition = ignitionOf(row.vehicle);
  const hasTelemetry = row.vehicle?.lastPosition != null;
  const detail = [row.equipo?.placa, row.equipo?.modelo ?? row.vehicle?.model].filter(Boolean).join(" · ");

  return (
    <li className="border-b border-gd-line last:border-b-0">
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={`relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
          selected ? "bg-gd-mist" : "bg-white hover:bg-gd-mist/70"
        }`}
      >
        {selected ? <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-gd-cyan" /> : null}
        <span
          aria-hidden="true"
          className={`flex size-11 shrink-0 items-center justify-center rounded-full ${
            row.state === "online" ? "bg-gd-cyan/15 text-gd-blue ring-2 ring-gd-cyan/60" : "bg-gd-mist text-gd-muted ring-1 ring-gd-line"
          }`}
        >
          <CarIcon className="size-6" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-[15px] font-semibold text-gd-navy">{row.name}</span>
          <span className={`block truncate text-[13px] font-medium ${ROW_STATE_CLASS[row.state]}`}>
            {ROW_STATE_LABEL[row.state]}
            {row.equipo === null ? <span className="font-semibold text-gd-blue"> · Sin registrar</span> : null}
          </span>
          {detail ? <span className="block truncate text-[12px] text-gd-muted">{detail}</span> : null}
        </span>

        <span className="flex shrink-0 items-center gap-1.5">
          <span
            title={ignitionText(ignition)}
            className={ignition === "on" ? "text-emerald-600" : ignition === "off" ? "text-gd-muted" : "text-zinc-300"}
          >
            <IgnitionIcon className="size-5" />
            <span className="sr-only">{ignitionText(ignition)}</span>
          </span>
          <span
            title={batteryText(battery)}
            className={
              battery === null
                ? "text-zinc-300"
                : battery < 20
                  ? "text-amber-600"
                  : battery < 50
                    ? "text-gd-cyan"
                    : "text-emerald-600"
            }
          >
            <BatteryIcon className="size-5" level={hasTelemetry ? battery : null} />
            <span className="sr-only">{batteryText(battery)}</span>
          </span>
        </span>
      </button>
    </li>
  );
}

function EmptyList({ title, body }: { title: string; body: string }) {
  return (
    <div className="px-6 py-12 text-center">
      <span
        aria-hidden="true"
        className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-gd-mist text-gd-blue"
      >
        <CarIcon className="size-7" />
      </span>
      <p className="mt-4 font-display text-[15px] font-bold text-gd-navy">{title}</p>
      <p className="mx-auto mt-1 max-w-xs text-sm text-gd-muted">{body}</p>
    </div>
  );
}
